import type { ZodType, ZodTypeDef } from "zod";
import { ApiErrorResponseSchema } from "@algoquest/contracts";

/**
 * Phase 10 — the game client's one HTTP boundary to `services/api`
 * (Master Spec §29/§30). The access token lives in a plain module-level
 * variable — not React state, not a Zustand store, and never
 * `localStorage` — matching the Product Owner's Phase 09 hybrid
 * token-storage decision exactly: a page refresh always loses it, which
 * is intentional. `App.tsx`'s boot sequence re-derives a fresh one from
 * the httpOnly refresh cookie via `authClient.ts`'s `refreshSession()`
 * every time the app (re)starts, rather than ever persisting it.
 *
 * This module deliberately does NOT retry on 401 itself — see
 * `authClient.ts`'s `withAuthRetry`, which owns the refresh-and-retry
 * policy. Splitting it out this way avoids a circular import: `authClient`
 * needs this module to call `/auth/refresh`, so this module cannot also
 * depend on `authClient` to react to a 401.
 */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

const DEFAULT_BASE_URL = "http://localhost:4000/api/v1";

function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;
  return configured && configured.length > 0 ? configured : DEFAULT_BASE_URL;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
  }
}

export interface ApiRequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
}

/**
 * `credentials: "include"` is what lets the browser attach the httpOnly
 * refresh cookie on `/auth/refresh` and `/auth/logout` (Phase 09 scoped
 * it to `path: "/api/v1/auth"`) — it is a harmless no-op on every other
 * request, since no other route ever reads that cookie.
 *
 * The response body is parsed against `responseSchema` on success (§10:
 * every trust boundary is Zod-validated, not just TypeScript-typed) and
 * against `ApiErrorResponseSchema` on failure, so a caller always gets
 * either a fully-typed success value or a thrown `ApiClientError` with a
 * real `code`/`message` — never a raw, unchecked `Response`.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions, responseSchema: ZodType<T, ZodTypeDef, unknown>): Promise<T> {
  const res = await fetch(`${resolveBaseUrl()}${path}`, {
    method: options.method ?? "GET",
    credentials: "include",
    signal: AbortSignal.timeout(15000),
    headers: {
      ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const json: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const parsedError = ApiErrorResponseSchema.safeParse(json);
    const error = parsedError.success
      ? parsedError.data.error
      : { code: "unknown_error", message: `Request failed with status ${res.status}.` };
    throw new ApiClientError(res.status, error.code, error.message);
  }

  return responseSchema.parse(json);
}
